import React, { useState, useRef, useEffect } from 'react';
import { Heart, X, User, MapPin, Calendar, Sparkles, Eye, Wine, Cigarette, Cross, Target, MessageCircle, Send, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { getDisplayName } from '../utils/displayName';
import axios from 'axios';
import { toast } from 'sonner';

const SwipeCard = ({ profile, onSwipe, isTop = false }) => {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [introMessage, setIntroMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const cardRef = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [photoIdx, setPhotoIdx] = useState(0);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const toUrl = (u) => (u && (u.startsWith('http') || u.startsWith('data:')) ? u : `${BACKEND_URL}${u}`);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`/users/${profile.id}/photos`);
        if (active) setPhotos(res.data || []);
      } catch (e) { /* member has no gallery */ }
    })();
    return () => { active = false; };
  }, [profile.id]);

  const photoUrls = photos.length
    ? photos.map((p) => toUrl(p.url))
    : (profile.profile_image ? [toUrl(profile.profile_image)] : []);
  const safeIdx = Math.min(photoIdx, Math.max(0, photoUrls.length - 1));

  const handleStart = (clientX, clientY) => {
    if (showProfileModal) return; // Don't allow dragging when modal is open
    setIsDragging(true);
    setStartPos({ x: clientX, y: clientY });
    setDragOffset({ x: 0, y: 0 });
  };

  const handleMove = (clientX, clientY) => {
    if (!isDragging || showProfileModal) return;

    const deltaX = clientX - startPos.x;
    const deltaY = clientY - startPos.y;
    
    setDragOffset({ x: deltaX, y: deltaY });
  };

  const handleEnd = () => {
    if (!isDragging || showProfileModal) return;
    setIsDragging(false);

    const threshold = 100;
    const { x } = dragOffset;

    if (Math.abs(x) > threshold) {
      // Swipe detected
      const direction = x > 0 ? 'right' : 'left';
      onSwipe(profile.id, direction);
    } else {
      // Snap back to center
      setDragOffset({ x: 0, y: 0 });
    }
  };

  // Mouse events
  const handleMouseDown = (e) => {
    if (showProfileModal) return;
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (showProfileModal) return;
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (showProfileModal) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    if (showProfileModal) return;
    handleEnd();
  };

  // Generate conversation ideas based on profile
  const getConversationIdeas = () => {
    const ideas = [];
    
    if (profile.mental_health_goals && profile.mental_health_goals.length > 0) {
      ideas.push(`Ask about their journey with ${profile.mental_health_goals[0].toLowerCase()}`);
    }
    if (profile.interests_hobbies && profile.interests_hobbies.length > 0) {
      ideas.push(`Talk about ${profile.interests_hobbies[0]} - you might share this interest!`);
    }
    if (profile.therapy_experience) {
      ideas.push("Share your thoughts on mental wellness and self-care");
    }
    if (profile.location) {
      ideas.push(`Ask about their favorite spots in ${profile.location.split(',')[0]}`);
    }
    
    // Add some default ideas
    ideas.push("What's something that made you smile recently?");
    ideas.push("What does a perfect relaxing day look like for you?");
    
    return ideas.slice(0, 4);
  };

  // Quick intro message templates
  const introTemplates = [
    `Hi ${getDisplayName(profile.full_name)}! I loved reading your profile 😊`,
    `Hey! I think we have a lot in common. Would love to chat!`,
    `Hi there! Your wellness journey sounds inspiring 🌱`,
    `Hello! I'm drawn to your positive energy ✨`,
  ];

  const handleSendIntroMessage = async () => {
    if (!introMessage.trim()) {
      toast.error('Please write a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      // First, like the profile
      await axios.post('/matches/swipe', {
        target_user_id: profile.id,
        action: 'like'
      });
      
      // Store the intro message to be sent when matched
      await axios.post('/matches/intro-message', {
        target_user_id: profile.id,
        message: introMessage
      });
      
      toast.success(`Message sent to ${getDisplayName(profile.full_name)}! They'll see it when you match 💕`);
      setShowMessageModal(false);
      setIntroMessage('');
      onSwipe(profile.id, 'right');
    } catch (error) {
      console.error('Error sending intro message:', error);
      // Even if intro message fails, the like was recorded
      toast.success(`Liked ${getDisplayName(profile.full_name)}!`);
      setShowMessageModal(false);
      onSwipe(profile.id, 'right');
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  const getRotation = () => {
    return dragOffset.x * 0.1; // Rotation based on horizontal drag
  };

  const getOpacity = () => {
    return isTop ? 1 : 0.8;
  };

  const getSwipeIndicator = () => {
    const absX = Math.abs(dragOffset.x);
    if (absX < 50) return null;

    return dragOffset.x > 0 ? (
      <div className="absolute top-8 left-8 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-lg transform rotate-12 shadow-lg">
        <Heart className="w-6 h-6 inline mr-2" />
        LIKE
      </div>
    ) : (
      <div className="absolute top-8 right-8 bg-red-500 text-white px-4 py-2 rounded-lg font-bold text-lg transform -rotate-12 shadow-lg">
        <X className="w-6 h-6 inline mr-2" />
        PASS
      </div>
    );
  };

  const getCompatibilityColor = (score) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-100';
    if (score >= 80) return 'text-emerald-600 bg-emerald-50';
    if (score >= 70) return 'text-amber-700 bg-amber-100';
    return 'text-orange-700 bg-orange-100';
  };

  return (
    <div
      ref={cardRef}
      className={`absolute inset-0 select-none cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
      style={{
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) rotate(${getRotation()}deg)`,
        opacity: getOpacity(),
        zIndex: isTop ? 20 : 10,
        transition: isDragging ? 'none' : 'transform 0.3s ease-out, opacity 0.3s ease-out'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="w-full h-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        {getSwipeIndicator()}
        
        {/* Profile Image Carousel */}
        <div className="relative h-64 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-800 dark:to-teal-800">
          {photoUrls.length ? (
            <img
              src={photoUrls[safeIdx]}
              alt={profile.full_name}
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-24 h-24 text-emerald-400 dark:text-emerald-300" />
            </div>
          )}

          {photoUrls.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setPhotoIdx((safeIdx - 1 + photoUrls.length) % photoUrls.length); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5"
                data-testid="swipe-photo-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setPhotoIdx((safeIdx + 1) % photoUrls.length); }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5"
                data-testid="swipe-photo-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                {photoUrls.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === safeIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
                ))}
              </div>
            </>
          )}

          {/* Compatibility Badge */}
          <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-bold ${
            getCompatibilityColor(profile.compatibility_score || 75)
          }`}>
            {Math.round(profile.compatibility_score || 75)}%
          </div>
        </div>

        {/* Profile Info */}
        <div className="p-6 space-y-4">
          {/* Name and Age */}
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">
                {getDisplayName(profile.full_name)}
              </h2>
              {profile.is_admin && (
                <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-full" title="Admin">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </span>
              )}
            </div>
            <div className="flex items-center space-x-4 text-emerald-600 dark:text-emerald-300 mt-2">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{profile.age} years old</span>
              </div>
              {profile.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div>
              <p className="text-emerald-700 dark:text-emerald-200 leading-relaxed">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Mental Health Goals */}
          {profile.mental_health_goals && profile.mental_health_goals.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                <Sparkles className="w-4 h-4 mr-1" />
                Mental Health Goals
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.mental_health_goals.slice(0, 4).map(goal => (
                  <span key={goal} className="px-3 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-sm rounded-full">
                    {goal}
                  </span>
                ))}
                {profile.mental_health_goals.length > 4 && (
                  <span className="px-3 py-1 bg-emerald-50 dark:bg-slate-700 text-emerald-600 dark:text-emerald-300 text-sm rounded-full">
                    +{profile.mental_health_goals.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Interests */}
          {profile.interests_hobbies && profile.interests_hobbies.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
                Interests & Hobbies
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.interests_hobbies.slice(0, 3).map(interest => (
                  <span key={interest} className="px-3 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-sm rounded-full">
                    {interest}
                  </span>
                ))}
                {profile.interests_hobbies.length > 3 && (
                  <span className="px-3 py-1 bg-teal-50 dark:bg-slate-700 text-teal-600 dark:text-teal-300 text-sm rounded-full">
                    +{profile.interests_hobbies.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Relationship Info */}
          {(profile.relationship_status || profile.looking_for) && (
            <div className="pt-2 border-t border-emerald-100 dark:border-slate-600">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {profile.relationship_status && (
                  <div>
                    <span className="text-emerald-600 dark:text-emerald-300 font-medium">Status:</span>
                    <br />
                    <span className="text-emerald-800 dark:text-emerald-100">{profile.relationship_status}</span>
                  </div>
                )}
                {profile.looking_for && (
                  <div>
                    <span className="text-emerald-600 dark:text-emerald-300 font-medium">Looking for:</span>
                    <br />
                    <span className="text-emerald-800 dark:text-emerald-100">{profile.looking_for}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowProfileModal(true);
              }}
              className="flex-1 py-2 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 rounded-lg font-medium hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
              data-testid="view-profile-btn"
            >
              <Eye className="w-4 h-4" />
              <span>View Profile</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMessageModal(true);
              }}
              className="flex-1 py-2 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 rounded-lg font-medium hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
              data-testid="send-message-btn"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Send Message</span>
            </button>
          </div>
        </div>
      </div>

      {/* Send Message Modal */}
      {showMessageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowMessageModal(false);
          }}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {profile.profile_image ? (
                    <img
                      src={toUrl(profile.profile_image)}
                      alt={getDisplayName(profile.full_name)}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold">Message {getDisplayName(profile.full_name)}</h2>
                    <p className="text-emerald-100 text-sm">Send a message with your like! 💚</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Quick Templates */}
              <div className="mb-4">
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-2 font-medium">Quick starters:</p>
                <div className="flex flex-wrap gap-2">
                  {introTemplates.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => setIntroMessage(template)}
                      className="px-3 py-1.5 bg-emerald-50 dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 text-xs rounded-full hover:bg-emerald-100 dark:hover:bg-slate-600 transition-colors"
                    >
                      {template.slice(0, 30)}...
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-4">
                <textarea
                  value={introMessage}
                  onChange={(e) => setIntroMessage(e.target.value)}
                  placeholder={`Write something nice to ${getDisplayName(profile.full_name)}...`}
                  className="w-full p-4 border border-emerald-200 dark:border-slate-600 rounded-xl bg-emerald-50 dark:bg-slate-700 text-emerald-800 dark:text-emerald-100 placeholder-emerald-400 dark:placeholder-emerald-500 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-emerald-500 dark:text-emerald-400 text-right mt-1">
                  {introMessage.length}/500
                </p>
              </div>

              {/* Preview */}
              {introMessage && (
                <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-100 dark:border-emerald-800">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-1">Preview:</p>
                  <p className="text-sm text-emerald-800 dark:text-emerald-100">{introMessage}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    onSwipe(profile.id, 'right');
                  }}
                  className="flex-1 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Like Only
                </button>
                <button
                  onClick={handleSendIntroMessage}
                  disabled={sendingMessage || !introMessage.trim()}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMessage ? (
                    <span>Sending...</span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Like + Message</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Profile Modal */}
      {showProfileModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowProfileModal(false);
          }}
        >
          <div 
            className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prominent Exit Button */}
            <button
              onClick={() => setShowProfileModal(false)}
              className="absolute top-4 right-4 z-10 p-3 bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-full shadow-lg transition-all hover:scale-110"
              data-testid="close-profile-modal"
            >
              <X className="w-6 h-6 text-gray-700 dark:text-gray-200" />
            </button>

            {/* Header with Photo */}
            <div className="relative h-48 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-800 dark:to-teal-800">
              {profile.profile_image ? (
                <img
                  src={toUrl(profile.profile_image)}
                  alt={getDisplayName(profile.full_name)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-20 h-20 text-emerald-400 dark:text-emerald-300" />
                </div>
              )}
              {/* Compatibility Badge */}
              <div className={`absolute bottom-4 left-4 px-3 py-1 rounded-full text-sm font-bold ${
                getCompatibilityColor(profile.compatibility_score || 75)
              }`}>
                {Math.round(profile.compatibility_score || 75)}% Match
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-12rem)]">
              {/* Name and Basic Info */}
              <div className="mb-6">
                <div className="flex items-center space-x-2">
                  <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">
                    {getDisplayName(profile.full_name)}
                  </h2>
                  {profile.is_admin && (
                    <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-full" title="Admin">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-emerald-600 dark:text-emerald-300 mt-2">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{profile.age} years old</span>
                  </div>
                  {profile.location && (
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{profile.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    About Me
                  </h3>
                  <p className="text-emerald-700 dark:text-emerald-200 leading-relaxed">
                    {profile.bio}
                  </p>
                </div>
              )}

              {/* Mental Health Goals */}
              {profile.mental_health_goals && profile.mental_health_goals.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Mental Health Goals
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.mental_health_goals.map(goal => (
                      <span key={goal} className="px-3 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-sm rounded-full">
                        {goal}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Interests */}
              {profile.interests_hobbies && profile.interests_hobbies.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                    <Heart className="w-4 h-4 mr-2" />
                    Interests & Hobbies
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.interests_hobbies.map(interest => (
                      <span key={interest} className="px-3 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-sm rounded-full">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Lifestyle Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-3 flex items-center">
                  <Target className="w-4 h-4 mr-2" />
                  Lifestyle & Preferences
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {profile.relationship_goals && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Relationship Goals</span>
                      <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.relationship_goals}</p>
                    </div>
                  )}
                  {profile.body_type && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Body Type</span>
                      <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.body_type}</p>
                    </div>
                  )}
                  {profile.alcohol && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                      <Wine className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <div>
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Alcohol</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.alcohol}</p>
                      </div>
                    </div>
                  )}
                  {profile.smoking && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                      <Cigarette className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <div>
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Smoking</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.smoking}</p>
                      </div>
                    </div>
                  )}
                  {profile.religious_beliefs && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                      <Cross className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <div>
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Religion</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.religious_beliefs}</p>
                      </div>
                    </div>
                  )}
                  {profile.therapy_experience && (
                    <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Therapy Experience</span>
                      <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{profile.therapy_experience}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Looking For */}
              {profile.looking_for_description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
                    What I'm Looking For
                  </h3>
                  <p className="text-emerald-700 dark:text-emerald-200 text-sm">
                    {profile.looking_for_description}
                  </p>
                </div>
              )}

              {/* Conversation Starter Ideas */}
              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-slate-700 dark:to-slate-600 rounded-xl">
                <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-3 flex items-center">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Conversation Ideas
                </h3>
                <ul className="space-y-2">
                  {getConversationIdeas().map((idea, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm">
                      <span className="text-emerald-500 mt-0.5">💡</span>
                      <span className="text-emerald-700 dark:text-emerald-200">{idea}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-emerald-100 dark:border-slate-600">
              <div className="flex space-x-3 mb-3">
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    onSwipe(profile.id, 'left');
                  }}
                  className="flex-1 py-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex items-center justify-center space-x-2"
                >
                  <X className="w-5 h-5" />
                  <span>Pass</span>
                </button>
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    onSwipe(profile.id, 'right');
                  }}
                  className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Heart className="w-5 h-5" />
                  <span>Like</span>
                </button>
              </div>
              <button
                onClick={() => setShowProfileModal(false)}
                className="w-full py-2 text-emerald-600 dark:text-emerald-400 font-medium hover:bg-emerald-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Back to Swiping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwipeCard;